import { type Diagnostic, lintGutter, linter } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';
import { jsonSchema } from '@langchain/codemirror-json-schema';

import { JSONSchema7 } from 'json-schema';
import { JsonError } from 'json-schema-library';
import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import { useDataset } from '@/hooks/useSwr';

const MOVE_TOOLTIP_THRESHOLD = 1; // don't move tooltip if the change is less than 1px

const customTooltipStyle = EditorView.theme(
  {
    '.cm-tooltip-arrow': {
      opacity: 0,
    },
    '.cm-tooltip': {
      zIndex: '1',
      maxWidth: '400px',
      wordBreak: 'break-word',
    },
    '.cm-tooltip-lint': {
      maxWidth: '400px',
      wordBreak: 'break-word',
    },
  },
  { dark: false }
);

export const jsonSchemaExtensions = (schema?: JSONSchema7) => [
  lintGutter(),
  ...(schema ? [jsonSchema(schema)] : []),
  customTooltipStyle,
];

// Convert JSON pointer to character position in text
function pointerToPos(text: string, pointer: string): number | null {
  try {
    const path = pointer.replace(/^#\//, '').split('/').filter(Boolean);
    if (path.length === 0) return 0;

    const lines = text.split('\n');

    // Simple approach: find the last path segment as a key in the JSON
    const lastSegment = path[path.length - 1];
    const isArrayIndex = /^\d+$/.test(lastSegment);

    if (!isArrayIndex) {
      // Look for the property key
      const keyPattern = new RegExp(`["']${lastSegment}["']\\s*:`);
      let charPos = 0;

      for (let i = 0; i < lines.length; i++) {
        if (keyPattern.test(lines[i])) {
          const match = lines[i].search(keyPattern);
          return charPos + match;
        }
        charPos += lines[i].length + 1; // +1 for newline
      }
    }

    return 0; // Fallback to start of document
  } catch (error) {
    console.error('Error converting pointer to position:', error);
    return null;
  }
}

// Convert JsonError[] to CodeMirror Diagnostics
function jsonErrorsToDiagnostics(
  errors: JsonError[],
  docText: string
): Diagnostic[] {
  return errors.map((error) => {
    const pointer = error.data?.pointer || '#';
    const pos = pointerToPos(docText, pointer) || 0;

    return {
      from: pos,
      to: pos + 1,
      severity: 'error',
      message: error.message,
    };
  });
}

export const useValidationErrorExtensions = (
  errors: JsonError[],
  value: string
) => {
  return useMemo(() => {
    // Create a linter that returns our validation errors as diagnostics
    const validationLinter = linter((view) => {
      const text = view.state.doc.toString();
      return jsonErrorsToDiagnostics(errors, text);
    });

    return [lintGutter(), validationLinter, customTooltipStyle];
  }, [errors, value]);
};

export const useCodeMirrorTooltipFollowMouse = (
  inputElementRef: RefObject<HTMLDivElement>,
  outputElementRef: RefObject<HTMLDivElement>,
  dataset_id: string | undefined,
  boundingRef?: RefObject<HTMLDivElement>
) => {
  const datasetSwr = useDataset(dataset_id);
  const inputs_schema_definition = datasetSwr.data?.inputs_schema_definition;
  const outputs_schema_definition = datasetSwr.data?.outputs_schema_definition;

  useCodeMirrorTooltipFollowMouseManual(
    inputElementRef,
    outputElementRef,
    !!inputs_schema_definition,
    !!outputs_schema_definition,
    boundingRef
  );
};

export const useCodeMirrorTooltipFollowMouseManual = (
  inputElementRef: RefObject<HTMLDivElement>,
  outputElementRef: RefObject<HTMLDivElement>,
  showOnInput: boolean,
  showOnOutput: boolean,
  boundingRef?: RefObject<HTMLDivElement>
) => {
  const mousePosition = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const updateMousePosition = (event: MouseEvent) => {
      mousePosition.current = { x: event.clientX, y: event.clientY };
    };

    document.addEventListener('mousemove', updateMousePosition);

    return () => {
      document.removeEventListener('mousemove', updateMousePosition);
    };
  }, []);

  const handleTooltipPosition = useCallback(
    (tooltip: HTMLElement, elementRef: RefObject<HTMLDivElement>) => {
      // Check if it has a listbox child - if so, there is an interactable element that we shouldnt move
      const listboxChild = tooltip.querySelector('[role="listbox"]');
      if (listboxChild !== null) {
        tooltip.style.transform = `translate(0px, 0px)`;
      } else {
        const { x, y } = mousePosition.current;
        if (!elementRef.current) return;
        const parentRect = elementRef.current.getBoundingClientRect();
        const left = parseFloat(tooltip.style.left);
        const top = parseFloat(tooltip.style.top);
        let tooltipX = x - left - parentRect.left + 10;
        let tooltipY = y - top - parentRect.top + 10;

        const boundingMaxX =
          boundingRef?.current?.getBoundingClientRect().right ??
          window.innerWidth;
        const boundingMaxY =
          boundingRef?.current?.getBoundingClientRect().bottom ??
          window.innerHeight;

        const tooltipRect = tooltip.getBoundingClientRect();
        if (
          tooltipX + parentRect.left + left + tooltipRect.width >
          boundingMaxX
        ) {
          tooltipX -= tooltipRect.width + 20;
          // Move left by the width of the tooltip plus a buffer
        }
        if (
          tooltipY + parentRect.top + top + tooltipRect.height >
          boundingMaxY
        ) {
          tooltipY -= tooltipRect.height + 20;
        }
        const currentTransform = tooltip.style.transform;
        const newTransform = `translate(${tooltipX}px, ${tooltipY}px)`;

        // Check if the position change is greater than the threshold
        const currentTransformMatch =
          currentTransform.match(/translate\(([^)]+)\)/);
        const newTransformMatch = newTransform.match(/translate\(([^)]+)\)/);

        if (currentTransformMatch && newTransformMatch) {
          const [currentX, currentY] = currentTransformMatch[1]
            .split(',')
            .map(parseFloat);
          const [newX, newY] = newTransformMatch[1].split(',').map(parseFloat);

          if (
            Math.abs(currentX - newX) < MOVE_TOOLTIP_THRESHOLD &&
            Math.abs(currentY - newY) < MOVE_TOOLTIP_THRESHOLD
          ) {
            return;
          }
        }
        tooltip.style.transform = newTransform;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    []
  );

  const setupTooltipObserver = useCallback(
    (tooltip: HTMLElement, elementRef: RefObject<HTMLDivElement>) => {
      const handleAttributeChange = (mutations: MutationRecord[]) => {
        for (const mutation of mutations) {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'style'
          ) {
            const left = parseFloat(tooltip.style.left);
            const top = parseFloat(tooltip.style.top);
            if (left !== 0 && top !== -10000) {
              handleTooltipPosition(tooltip, elementRef);
            }
          }
        }
      };

      const attributeObserver = new MutationObserver(handleAttributeChange);
      attributeObserver.observe(tooltip, { attributes: true });

      return attributeObserver;
    },
    [handleTooltipPosition]
  );

  const setupMutationObserver = useCallback(
    (elementRef: React.RefObject<HTMLDivElement>): MutationObserver[] => {
      const observers: MutationObserver[] = [];
      const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (
                node.nodeType === 1 &&
                'classList' in node &&
                typeof node.classList === 'function' &&
                (node as HTMLElement).classList.contains('cm-tooltip')
              ) {
                handleTooltipPosition(node as HTMLElement, elementRef);
                const tooltipObserver = setupTooltipObserver(
                  node as HTMLElement,
                  elementRef
                );
                observers.push(tooltipObserver);
              }
            });
          }
        }
      });

      if (elementRef.current) {
        observer.observe(elementRef.current, {
          childList: true,
          subtree: true,
        });
        observers.push(observer);
      }

      return observers;
    },
    [handleTooltipPosition, setupTooltipObserver]
  );

  useEffect(() => {
    let inputObservers: MutationObserver[] = [];
    if (showOnInput) {
      inputObservers = setupMutationObserver(inputElementRef);
    }

    let outputObservers: MutationObserver[] = [];
    if (showOnOutput) {
      outputObservers = setupMutationObserver(outputElementRef);
    }

    return () => {
      inputObservers.forEach((observer) => observer.disconnect());
      outputObservers.forEach((observer) => observer.disconnect());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnInput, showOnOutput, setupMutationObserver]); // If showOnInput or showOnOutput changes, we might need to add or remove observers

  useEffect(() => {
    const handleInputMouseMove = (event: MouseEvent) => {
      const container = inputElementRef.current;
      if (!container) {
        return;
      }
      const tooltip = document.querySelector('.cm-tooltip');
      if (tooltip && tooltip instanceof HTMLElement) {
        const parentRect = inputElementRef.current.getBoundingClientRect();
        const left = parseFloat(tooltip.style.left || '0');
        const top = parseFloat(tooltip.style.top || '0');
        const x = event.clientX - left - parentRect.left + 10;
        const y = event.clientY - top - parentRect.top + 10;

        tooltip.style.transform = `translate(${x}px, ${y}px)`;
      }
    };

    const handleOutputMouseMove = (event: MouseEvent) => {
      const container = outputElementRef.current;
      if (!container) {
        return;
      }
      const tooltip = document.querySelector('.cm-tooltip');

      if (tooltip && tooltip instanceof HTMLElement) {
        const listBoxChild = tooltip.querySelector('[role="listbox"]');
        if (listBoxChild !== null) {
          tooltip.style.transform = `translate(0px, 0px)`;
        } else {
          const parentRect = outputElementRef.current.getBoundingClientRect();
          const left = parseFloat(tooltip.style.left || '0');
          const top = parseFloat(tooltip.style.top || '0');
          const x = event.clientX - left - parentRect.left + 10;
          const y = event.clientY - top - parentRect.top + 10;

          tooltip.style.transform = `translate(${x}px, ${y}px)`;
        }
      }
    };

    const inputEl = inputElementRef.current;
    const outputEl = outputElementRef.current;
    if (inputEl && showOnInput) {
      inputEl.addEventListener('mousemove', handleInputMouseMove);
    }
    if (outputEl && showOnOutput) {
      outputEl.addEventListener('mousemove', handleOutputMouseMove);
    }

    return () => {
      if (inputEl) {
        inputEl.removeEventListener('mousemove', handleInputMouseMove);
      }
      if (outputEl) {
        outputEl.removeEventListener('mousemove', handleOutputMouseMove);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnInput, showOnOutput]);
};
